/**
 * CallRegisteredPage — /calls/:callSlug/registered
 *
 * Thank-you page after registering for a registration-gated call (town hall).
 * Shows the confirmed seat, the call time in the visitor's own time zone,
 * add-to-calendar buttons (Google + ICS download), and what happens next.
 * Guarded by the localStorage flag the RSVP form sets — visitors who haven't
 * registered are bounced back to the landing page.
 */

import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import TakerShell from './components/TakerShell';
import { NewsletterOptIn } from './components/NewsletterOptIn';
import { offersNewsletterOnCall } from './lib/newsletterOptIn';

const API_BASE = (import.meta as unknown as { env: Record<string, string> }).env?.VITE_API_BASE_URL ?? '';

interface CallData {
  slug: string;
  name: string;
  display_name: string | null;
  tagline: string | null;
  blurb: string | null;
  description: string | null;
  duration_minutes: number;
}

interface NextOccurrence {
  start_utc: string;
  timezone: string;
  duration_minutes: number;
}

type PageState =
  | { phase: 'loading' }
  | { phase: 'error'; message: string }
  | { phase: 'ready'; call: CallData; next: NextOccurrence | null };

function fmt(d: Date, tz: string | undefined, opts: Intl.DateTimeFormatOptions): string {
  return new Intl.DateTimeFormat('en-US', { ...(tz ? { timeZone: tz } : {}), ...opts }).format(d);
}

/** YYYYMMDDTHHMMSSZ stamp for calendar links. */
function calStamp(d: Date): string {
  return d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
}

export default function CallRegisteredPage() {
  const { callSlug } = useParams<{ callSlug: string }>();
  const navigate = useNavigate();
  const [state, setState] = useState<PageState>({ phase: 'loading' });

  // Only meaningful for people who actually registered here.
  useEffect(() => {
    if (!callSlug) return;
    let registered = false;
    try { registered = !!window.localStorage.getItem(`call-rsvp:${callSlug}`); } catch { registered = true; }
    if (!registered) navigate(`/calls/${callSlug}`, { replace: true });
  }, [callSlug, navigate]);

  useEffect(() => {
    if (!callSlug) { setState({ phase: 'error', message: 'No call specified.' }); return; }
    fetch(`${API_BASE}/api/public/calls/${encodeURIComponent(callSlug)}`)
      .then(async (r) => {
        if (!r.ok) throw new Error(r.status === 404 ? 'This call was not found.' : `Server error ${r.status}`);
        const d = await r.json() as { call: CallData; next_occurrence: NextOccurrence | null };
        setState({ phase: 'ready', call: d.call, next: d.next_occurrence ?? null });
      })
      .catch((err) => setState({ phase: 'error', message: err instanceof Error ? err.message : String(err) }));
  }, [callSlug]);

  if (state.phase !== 'ready') {
    return (
      <TakerShell>
        <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ color: state.phase === 'error' ? '#991b1b' : '#6b7280', fontSize: 14 }}>
            {state.phase === 'error' ? state.message : 'Loading…'}
          </div>
        </div>
      </TakerShell>
    );
  }

  const { call, next } = state;
  const title = call.display_name || call.name;
  const start = next ? new Date(next.start_utc) : null;
  const tz = next?.timezone || 'America/Toronto';
  const callPagePath = `/calls/${encodeURIComponent(call.slug)}`;

  const dayLong = start ? fmt(start, tz, { weekday: 'long', month: 'long', day: 'numeric' }) : null;
  const timeShort = start ? fmt(start, tz, { hour: 'numeric', minute: '2-digit', timeZoneName: 'short' }) : null;
  // Visitor-local line, only when it differs from the call-zone display.
  let visitorLocal: string | null = null;
  if (start) {
    try {
      const stamp = (z?: string) => fmt(start, z, { weekday: 'short', hour: 'numeric', minute: '2-digit' });
      if (stamp(undefined) !== stamp(tz)) {
        visitorLocal = fmt(start, undefined, {
          weekday: 'long', month: 'long', day: 'numeric',
          hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
        });
      }
    } catch { /* call-zone display stands on its own */ }
  }

  // Add-to-calendar — the calendar shows it in their local time forever.
  const pageUrl = `${window.location.origin}${callPagePath}`;
  const details = `${call.tagline ?? title} The Zoom join button opens on the call page 15 minutes before the start: ${pageUrl}`;
  let googleHref: string | null = null;
  let icsHref: string | null = null;
  if (start && next) {
    const end = new Date(start.getTime() + (next.duration_minutes || 60) * 60_000);
    const dates = `${calStamp(start)}/${calStamp(end)}`;
    googleHref = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(title)}&dates=${dates}&details=${encodeURIComponent(details)}&location=${encodeURIComponent('Zoom')}`;
    const ics = [
      'BEGIN:VCALENDAR', 'VERSION:2.0',
      'PRODID:-//The Successful Bookkeeper//Calls//EN',
      'BEGIN:VEVENT',
      `UID:call-${call.slug}-${calStamp(start)}@thesuccessfulbookkeeper.com`,
      `DTSTAMP:${calStamp(new Date())}`,
      `DTSTART:${calStamp(start)}`,
      `DTEND:${calStamp(end)}`,
      `SUMMARY:${title}`,
      `DESCRIPTION:${details.replace(/,/g, '\\,')}`,
      `URL:${pageUrl}`,
      'LOCATION:Zoom',
      'END:VEVENT', 'END:VCALENDAR',
    ].join('\r\n');
    icsHref = `data:text/calendar;charset=utf-8,${encodeURIComponent(ics)}`;
  }

  const steps: Array<{ title: string; body: string }> = [
    { title: 'Confirmation email — on its way.', body: 'It includes a calendar invite that lands in your time zone automatically. If it has not arrived in a few minutes, check your spam folder.' },
    { title: 'Reminders before the call.', body: 'We will email you the day before and an hour before, with a link back to this call page — no calendar hunting.' },
    { title: 'Joining on the day.', body: 'The Zoom join button appears on the call page 15 minutes before the start — that is the one place to join from.' },
  ];

  return (
    <TakerShell footerLabel="Free with registration">
      <section style={{
        background: 'linear-gradient(160deg, #0A0A2E 0%, #14143d 100%)',
        color: '#fff', padding: '64px 32px 72px', textAlign: 'center',
        fontFamily: "'Poppins', sans-serif",
      }}>
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 8,
          background: 'rgba(74,222,128,0.14)', border: '1px solid rgba(74,222,128,0.4)',
          color: '#4ade80', fontSize: 12, fontWeight: 700, letterSpacing: 1,
          textTransform: 'uppercase' as const, padding: '6px 18px', borderRadius: 999, marginBottom: 26,
        }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#4ade80' }} />
          You're registered
        </div>
        <h1 style={{
          fontFamily: "'Fraunces', serif",
          fontSize: 'clamp(30px, 5vw, 46px)', fontWeight: 900, lineHeight: 1.12,
          maxWidth: 680, margin: '0 auto 14px',
        }}>
          Your seat is saved for {title}
        </h1>
        {dayLong && (
          <p style={{ fontSize: 17, color: '#c5ccd6', marginBottom: visitorLocal ? 6 : 26 }}>
            <strong style={{ color: '#fff' }}>{dayLong}</strong> · {timeShort}
          </p>
        )}
        {visitorLocal && (
          <p style={{ fontSize: 14, color: '#9ca3af', marginBottom: 26 }}>
            For you, that's <strong style={{ color: '#e2e8f0' }}>{visitorLocal}</strong>
          </p>
        )}
        {googleHref && icsHref && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: 12, flexWrap: 'wrap' as const }}>
            <a href={googleHref} target="_blank" rel="noopener noreferrer" style={{
              background: '#F05A28', color: '#fff', fontWeight: 700, fontSize: 14,
              padding: '13px 26px', borderRadius: 8, textDecoration: 'none',
            }}>+ Google Calendar</a>
            <a href={icsHref} download={`${call.slug}.ics`} style={{
              background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.25)',
              color: '#fff', fontWeight: 600, fontSize: 14,
              padding: '13px 26px', borderRadius: 8, textDecoration: 'none',
            }}>+ Apple / Outlook</a>
          </div>
        )}
      </section>

      <div style={{ maxWidth: 720, margin: '0 auto', padding: '48px 24px 80px', fontFamily: "'Poppins', sans-serif" }}>
        <h2 style={{ fontSize: 22, fontWeight: 700, color: '#0A0A2E', marginBottom: 18 }}>What happens next</h2>
        {steps.map((s, i) => (
          <div key={i} style={{ display: 'flex', gap: 16, padding: '16px 0', borderBottom: i === steps.length - 1 ? 'none' : '1px solid #f0ece7' }}>
            <div style={{
              flex: 'none', width: 28, height: 28, borderRadius: '50%', background: '#FFE4D6',
              color: '#F05A28', fontWeight: 700, fontSize: 14,
              display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 1,
            }}>{i + 1}</div>
            <div style={{ fontSize: 15, lineHeight: 1.65, color: '#4b5563' }}>
              <strong style={{ color: '#0A0A2E' }}>{s.title}</strong>{' '}{s.body}
            </div>
          </div>
        ))}
        {offersNewsletterOnCall(callSlug) && (
          <NewsletterOptIn
            email={(() => { try { return window.sessionStorage.getItem(`call-rsvp-email:${callSlug}`); } catch { return null; } })()}
            source="call-rsvp" sourceDetail={callSlug!} page="thank-you"
            style={{ marginTop: 24, color: '#4b5563' }}
          />
        )}
        <div style={{ marginTop: 32, textAlign: 'center' }}>
          <Link to={callPagePath} style={{ color: '#F05A28', fontWeight: 600, fontSize: 14, textDecoration: 'none' }}>
            ← Back to the call page
          </Link>
        </div>
      </div>
    </TakerShell>
  );
}
